import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, CircleDot, IdCard, ShieldAlert, ShieldX, Swords, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Club, Player, PlayerDetail, PlayerMatchHistoryEntry } from "@/types";
import { useAuthStore } from "@/store/auth";
import { SPAIN_COMUNIDADES, SPAIN_PROVINCES_BY_COMUNIDAD } from "@/lib/spainGeo";
import { Card, Badge } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { ContactButtons } from "@/components/ContactButtons";

const dateFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Misma clase que ya usa el selector de club en UsersPage — ningún <select> del panel se sale de
// esta línea visual.
const selectClass =
  "rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

interface PaginatedPlayers {
  data: Player[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortKey =
  | "nombre"
  | "apellidos"
  | "club"
  | "partidosJugados"
  | "partidosGanados"
  | "partidosPerdidos"
  | "puntosGanados"
  | "erroresForzados"
  | "erroresNoForzados";

// Mismo patrón que ya usa ClubsPage para columnas clicables: toda la tabla se puede ordenar por
// cualquier columna, no solo por el viejo botón fijo de "Clasificación"/"A-Z".
function SortHeader({ label, active, dir, onClick }: { label: string; active: boolean; dir: "asc" | "desc"; onClick: () => void }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink-700 ${active ? "text-ink-700" : ""}`}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

const RESULT_LABEL: Record<PlayerMatchHistoryEntry["resultado"], { label: string; tone: "green" | "red" | "slate" }> = {
  ganado: { label: "Ganado", tone: "green" },
  perdido: { label: "Perdido", tone: "red" },
  en_curso: { label: "En curso", tone: "slate" },
  finalizado: { label: "Finalizado", tone: "slate" },
};

function StatBox({ icon: Icon, label, value, tone }: { icon: typeof Trophy; label: string; value: number; tone: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl bg-ink-50/70 px-3 py-3 text-center">
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="text-lg font-bold text-ink-900">{value}</p>
      <p className="text-[11px] text-ink-400">{label}</p>
    </div>
  );
}

function HistoryEntry({ entry }: { entry: PlayerMatchHistoryEntry }) {
  const result = RESULT_LABEL[entry.resultado];
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-ink-100 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink-800">{entry.pista ?? "Pista sin asignar"}</p>
          <p className="text-xs text-ink-400">{dateFormatter.format(new Date(entry.fecha))}</p>
        </div>
        <Badge tone={result.tone}>{result.label}</Badge>
      </div>
      <p className="text-xs text-ink-500">
        Con <span className="font-medium text-ink-700">{entry.companero}</span> contra{" "}
        <span className="font-medium text-ink-700">{entry.rivales.join(" / ")}</span>
      </p>
      <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
        <span className="font-semibold text-ink-700">
          Sets {entry.setsA} – {entry.setsB}
        </span>
        <span>·</span>
        <span>{entry.puntosGanados} puntos limpios</span>
        <span>·</span>
        <span>{entry.erroresForzados} errores forzados</span>
        <span>·</span>
        <span>{entry.erroresNoForzados} errores no forzados</span>
      </div>
    </div>
  );
}

function ContactoForm({ player }: { player: PlayerDetail }) {
  const queryClient = useQueryClient();
  const [nombre, setNombre] = useState(player.nombre);
  const [apellidos, setApellidos] = useState(player.apellidos ?? "");
  const [telefono, setTelefono] = useState(player.telefono ?? "");
  const [email, setEmail] = useState(player.email ?? "");
  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/players/${player.id}`, {
        nombre: nombre.trim(),
        apellidos: apellidos.trim() || null,
        telefono: telefono || null,
        email: email || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", player.id] });
      queryClient.invalidateQueries({ queryKey: ["players"] });
      toast.success("Contacto actualizado");
      setEditing(false);
    },
    onError: () => toast.error("No se pudo guardar el contacto"),
  });

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl bg-ink-50/70 px-3 py-2">
        <div className="text-sm text-ink-600">
          {player.telefono || player.email ? (
            <span>
              {player.telefono && <span className="mr-3">{player.telefono}</span>}
              {player.email && <span>{player.email}</span>}
            </span>
          ) : (
            <span className="text-ink-400">Sin teléfono ni email</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ContactButtons telefono={player.telefono} email={player.email} size="md" />
          <button onClick={() => setEditing(true)} className="text-xs font-medium text-brand-600 hover:underline">
            Editar
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-3 rounded-xl bg-ink-50/70 p-3"
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className="sm:flex-1" required />
        <Input label="Apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} className="sm:flex-1" />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Input label="Teléfono" placeholder="600 000 000" value={telefono} onChange={(e) => setTelefono(e.target.value)} className="sm:w-40" />
        <Input label="Email" type="email" placeholder="jugador@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="sm:flex-1" />
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            Guardar
          </Button>
          <Button type="button" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
      </div>
    </form>
  );
}

function PlayerDetailModal({ playerId, onOpenChange }: { playerId: number; onOpenChange: (open: boolean) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["players", playerId],
    queryFn: async () => (await api.get<PlayerDetail>(`/players/${playerId}`)).data,
  });

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={data ? `${data.nombre}${data.apellidos ? ` ${data.apellidos}` : ""}` : "Jugador"}
      description={data?.club.nombre}
    >
      {isLoading || !data ? (
        <p className="text-sm text-ink-400">Cargando…</p>
      ) : (
        <div className="flex flex-col gap-5">
          <ContactoForm player={data} />
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            <StatBox icon={Swords} label="Jugados" value={data.stats.partidosJugados} tone="bg-brand-50 text-brand-600" />
            <StatBox icon={Trophy} label="Ganados" value={data.stats.partidosGanados} tone="bg-emerald-50 text-emerald-600" />
            <StatBox icon={ShieldX} label="Perdidos" value={data.stats.partidosPerdidos} tone="bg-red-50 text-red-600" />
            <StatBox icon={CircleDot} label="Puntos limpios" value={data.stats.puntosGanados} tone="bg-violet-50 text-violet-600" />
            <StatBox icon={ShieldAlert} label="Errores forzados" value={data.stats.erroresForzados} tone="bg-amber-50 text-amber-600" />
            <StatBox icon={ShieldAlert} label="Errores no forzados" value={data.stats.erroresNoForzados} tone="bg-orange-50 text-orange-600" />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-ink-600">Historial de partidos</p>
            {!data.historial.length ? (
              <p className="text-sm text-ink-400">Todavía no ha jugado ningún partido con el marcador.</p>
            ) : (
              <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto pr-1">
                {data.historial.map((entry) => (
                  <HistoryEntry key={entry.partidoId} entry={entry} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

const MEDALLA = ["🥇", "🥈", "🥉"];

export function PlayersPage() {
  const role = useAuthStore((s) => s.user?.role);
  const showClubColumn = role === "SUPER_ADMIN" || role === "PROVIDER";
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [clubId, setClubId] = useState<number | undefined>(undefined);
  const [nombreFiltro, setNombreFiltro] = useState("");
  const [apellidosFiltro, setApellidosFiltro] = useState("");
  const [pais, setPais] = useState("");
  const [comunidadAutonoma, setComunidadAutonoma] = useState("");
  const [provincia, setProvincia] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [openPlayerId, setOpenPlayerId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>("partidosGanados");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function toggleSort(key: SortKey) {
    setSortDir((dir) => (sortBy === key ? (dir === "asc" ? "desc" : "asc") : key === "nombre" || key === "apellidos" || key === "club" ? "asc" : "desc"));
    setSortBy(key);
    setPage(1);
  }

  const { data: clubs } = useQuery({
    queryKey: ["clubs"],
    queryFn: async () => (await api.get<{ data: Club[] }>("/clubs?limit=100")).data.data,
    enabled: showClubColumn,
  });

  function filterParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (clubId) params.set("clubId", String(clubId));
    if (nombreFiltro.trim()) params.set("nombre", nombreFiltro.trim());
    if (apellidosFiltro.trim()) params.set("apellidos", apellidosFiltro.trim());
    if (pais) params.set("pais", pais);
    if (comunidadAutonoma) params.set("comunidadAutonoma", comunidadAutonoma);
    if (provincia) params.set("provincia", provincia);
    if (localidad.trim()) params.set("localidad", localidad.trim());
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir);
    return params;
  }

  function clearFilters() {
    setClubId(undefined);
    setNombreFiltro("");
    setApellidosFiltro("");
    setPais("");
    setComunidadAutonoma("");
    setProvincia("");
    setLocalidad("");
    setPage(1);
  }

  const { data: resp, isLoading } = useQuery({
    queryKey: ["players", { page, limit, clubId, nombreFiltro, apellidosFiltro, pais, comunidadAutonoma, provincia, localidad, sortBy, sortDir }],
    queryFn: async () => {
      const params = filterParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedPlayers>(`/players?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const hayFiltrosActivos = Boolean(
    clubId || nombreFiltro.trim() || apellidosFiltro.trim() || pais || comunidadAutonoma || provincia || localidad.trim()
  );

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Jugadores" }]} />

      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <IdCard className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Jugadores</h1>
          <p className="text-ink-400">Ficha, historial de partidos y estadísticas de cada jugador.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {showClubColumn && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Club</label>
              <select
                value={clubId ?? ""}
                onChange={(e) => {
                  setClubId(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                className={`${selectClass} w-full`}
              >
                <option value="">Todos los clubes</option>
                {clubs?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            label="Nombre"
            placeholder="Buscar por nombre…"
            value={nombreFiltro}
            onChange={(e) => {
              setNombreFiltro(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
          <Input
            label="Apellidos"
            placeholder="Buscar por apellidos…"
            value={apellidosFiltro}
            onChange={(e) => {
              setApellidosFiltro(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
          <Input
            label="País"
            placeholder="España"
            value={pais}
            onChange={(e) => {
              setPais(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Comunidad autónoma</label>
            <select
              value={comunidadAutonoma}
              onChange={(e) => {
                setComunidadAutonoma(e.target.value);
                setProvincia("");
                setPage(1);
              }}
              className={`${selectClass} w-full`}
            >
              <option value="">Todas</option>
              {SPAIN_COMUNIDADES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Provincia</label>
            <select
              value={provincia}
              disabled={!comunidadAutonoma}
              onChange={(e) => {
                setProvincia(e.target.value);
                setPage(1);
              }}
              className={`${selectClass} w-full`}
            >
              <option value="">Todas</option>
              {(SPAIN_PROVINCES_BY_COMUNIDAD[comunidadAutonoma] ?? []).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Localidad"
            placeholder="Buscar por localidad…"
            value={localidad}
            onChange={(e) => {
              setLocalidad(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {hayFiltrosActivos ? <Button onClick={clearFilters}>Limpiar filtros</Button> : <span />}
          <ExportExcelButton url={`/players/export.xlsx?${filterParams().toString()}`} filename="jugadores.xlsx" />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-400">Cargando…</p>
      ) : !resp || !resp.data.length ? (
        <Card className="border-dashed bg-white/60">
          <p className="text-sm text-ink-400">
            {hayFiltrosActivos ? "Ningún jugador coincide con los filtros." : "Todavía no hay jugadores registrados."}
          </p>
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-ink-100 bg-white shadow-sm shadow-ink-900/[0.03]">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">
                    <SortHeader label="Nombre" active={sortBy === "nombre"} dir={sortDir} onClick={() => toggleSort("nombre")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Apellidos" active={sortBy === "apellidos"} dir={sortDir} onClick={() => toggleSort("apellidos")} />
                  </th>
                  {showClubColumn && (
                    <th className="px-4 py-3">
                      <SortHeader label="Club" active={sortBy === "club"} dir={sortDir} onClick={() => toggleSort("club")} />
                    </th>
                  )}
                  <th className="px-4 py-3">
                    <SortHeader label="Jugados" active={sortBy === "partidosJugados"} dir={sortDir} onClick={() => toggleSort("partidosJugados")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Ganados" active={sortBy === "partidosGanados"} dir={sortDir} onClick={() => toggleSort("partidosGanados")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Perdidos" active={sortBy === "partidosPerdidos"} dir={sortDir} onClick={() => toggleSort("partidosPerdidos")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Puntos limpios" active={sortBy === "puntosGanados"} dir={sortDir} onClick={() => toggleSort("puntosGanados")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Err. forzados" active={sortBy === "erroresForzados"} dir={sortDir} onClick={() => toggleSort("erroresForzados")} />
                  </th>
                  <th className="px-4 py-3">
                    <SortHeader label="Err. no forzados" active={sortBy === "erroresNoForzados"} dir={sortDir} onClick={() => toggleSort("erroresNoForzados")} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {resp.data.map((p, i) => {
                  const rank = (resp.page - 1) * resp.limit + i + 1;
                  const esRankingPrincipal = sortBy === "partidosGanados" && sortDir === "desc";
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setOpenPlayerId(p.id)}
                      className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-brand-50/40"
                    >
                      <td className="px-4 py-3 font-semibold text-ink-500">
                        {esRankingPrincipal ? (MEDALLA[rank - 1] ?? rank) : rank}
                      </td>
                      <td className="px-4 py-3 font-medium text-ink-800">{p.nombre}</td>
                      <td className="px-4 py-3 font-medium text-ink-800">
                        <span className="flex items-center gap-2">
                          {p.apellidos ?? <span className="text-ink-300">—</span>}
                          <ContactButtons telefono={p.telefono} email={p.email} />
                        </span>
                      </td>
                      {showClubColumn && <td className="px-4 py-3 text-ink-500">{p.club.nombre}</td>}
                      <td className="px-4 py-3 text-ink-500">{p.partidosJugados}</td>
                      <td className="px-4 py-3 text-emerald-600">{p.partidosGanados}</td>
                      <td className="px-4 py-3 text-red-500">{p.partidosPerdidos}</td>
                      <td className="px-4 py-3 text-ink-500">{p.puntosGanados}</td>
                      <td className="px-4 py-3 text-ink-500">{p.erroresForzados}</td>
                      <td className="px-4 py-3 text-ink-500">{p.erroresNoForzados}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={resp.page}
            totalPages={resp.totalPages}
            total={resp.total}
            onPageChange={setPage}
            limit={limit}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </>
      )}

      {openPlayerId !== null && <PlayerDetailModal playerId={openPlayerId} onOpenChange={(open) => !open && setOpenPlayerId(null)} />}
    </div>
  );
}
